import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function TableNo33() {
  const pdfRef = useRef();

  // ✅ Data
  const employmentRaceData = [
    {
      raceLabel: "American Indian or Alaska Native Alone",
      year2018: 1591,
      year2024: 1965,
    },
    { raceLabel: "Asian Alone", year2018: 6377, year2024: 7047 },
    {
      raceLabel: "Black or African American Alone",
      year2018: 2346,
      year2024: 4154,
    },
    {
      raceLabel: "Native Hawaiian or Other Pacific Islander Alone",
      year2018: 1370,
      year2024: 1393,
    },
    { raceLabel: "Two or More Race Groups", year2018: 1821, year2024: 2790 },
    { raceLabel: "White Alone", year2018: 103674, year2024: 120032 },
  ];

  // ✅ Derived insights
  const dataWithGrowth = employmentRaceData.map((r) => ({
    ...r,
    growth: (((r.year2024 - r.year2018) / r.year2018) * 100).toFixed(2),
  }));

  const highest = dataWithGrowth.reduce((a, b) =>
    +a.growth > +b.growth ? a : b
  );
  const lowest = dataWithGrowth.reduce((a, b) =>
    +a.growth < +b.growth ? a : b
  );

  // ✅ Chart Setup
  const chartData = {
    labels: dataWithGrowth.map((r) => r.raceLabel),
    datasets: [
      {
        label: "2018",
        data: dataWithGrowth.map((r) => r.year2018),
        backgroundColor: "#FFF085",
        borderRadius: 5,
      },
      {
        label: "2024",
        data: dataWithGrowth.map((r) => r.year2024),
        backgroundColor: "#82181A",
        borderRadius: 5,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employment by Race (2018 vs 2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `${ctx.dataset.label}: ${ctx.raw.toLocaleString("en-US")} employees`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        ticks: { autoSkip: false, maxRotation: 45 },
      },
    },
  };

  // ✅ PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("employment_by_race_2018_2024.pdf");
      // input.style.width = originalWidth;
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-lg"
      >
        {/* Title */}
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment by Race (2018 vs 2024)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Highest Growth</p>
            <p className="text-lg font-bold text-green-800">
              {highest.raceLabel} (+{highest.growth}%)
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Lowest Growth</p>
            <p className="text-lg font-bold text-red-800">
              {lowest.raceLabel} ({lowest.growth}%)
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-blue-700 text-white">
                <tr>
                  <th className="p-2">Race</th>
                  <th className="p-2">2018</th>
                  <th className="p-2">2024</th>
                  <th className="p-2">Growth (%)</th>
                </tr>
              </thead>
              <tbody>
                {dataWithGrowth.map((r) => (
                  <tr
                    key={r.raceLabel}
                    className={`hover:bg-gray-100 transition ${
                      +r.growth > 0 ? "bg-green-50" : "bg-red-50"
                    }`}
                  >
                    <td className="p-2 font-medium">{r.raceLabel}</td>
                    <td className="p-2">
                      {r.year2018.toLocaleString("en-US")}
                    </td>
                    <td className="p-2">
                      {r.year2024.toLocaleString("en-US")}
                    </td>
                    <td
                      className={`p-2 font-semibold ${
                        +r.growth > 0 ? "text-green-700" : "text-red-700"
                      }`}
                    >
                      {+r.growth > 0 ? "+" : ""}
                      {r.growth}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary */}
        <p className="text-center text-gray-600">
          📊 Between 2018 and 2024,{" "}
          <span className="font-semibold text-green-700">
            {highest.raceLabel}
          </span>{" "}
          showed the highest employment growth (+{highest.growth}%), while{" "}
          <span className="font-semibold text-red-700">{lowest.raceLabel}</span>{" "}
          had the lowest change ({lowest.growth}%).
        </p>
      </div>

      {/* PDF Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
